/**
 * QR 스티커 통합 페이지 - 가로 2분할 레이아웃
 *
 * 좌측 (40%): QR 카메라 스캐너 + USB 리더기 (항상 활성)
 * 우측 (60%): 전화번호 입력 (넘패드) + 출력 로그
 *
 * 모든 입력(카메라/리더기/전화번호) → 가족 팝업 → 선택 출력
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import QRScanner from '../components/QRScanner';
import {
  printQrBatch, getPrinterStatus, getStorePrinters, checkUsbBridge, reportUsbPrintError,
  getMemberByPhone, getMemberByQR, getFamilyMembers,
} from '../services/api';
import { extractQRCode } from '../utils/qrUtils';
import './QrStickerPage.css';

import { isBridgeApp } from '../utils/isBridgeApp';
const COOLDOWN_MS = 5000;
const MAX_LOGS = 5;
const LOG_TTL_MS = 10000;
const PRINTER_POLL_INTERVAL = 30000;

const NUMPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['clear', '0', 'backspace'],
];

const STORE_NAMES = {
  '1': '영등포점', '2': '경주점', '3': '동탄점', '4': '하남점',
};

export default function QrStickerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [printerStatus, setPrinterStatus] = useState(null);
  const [printerError, setPrinterError] = useState(null);
  const cooldownMap = useRef(new Map());
  const pollRef = useRef(null);

  // 프린터 선택
  const [printerList, setPrinterList] = useState([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState(null);

  // 전화번호 상태
  const [digits, setDigits] = useState('');
  const [phoneError, setPhoneError] = useState(null);

  // 가족 팝업 상태
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupError, setPopupError] = useState(null);
  const [popupMemberInfo, setPopupMemberInfo] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // 프린터 목록 로드 (USB 프린터가 있을 때만 브릿지 상태 확인)
  useEffect(() => {
    (async () => {
      try {
        const res = await getStorePrinters();
        const printers = res.printers || [];
        const hasUsb = printers.some(p => p.connection_type === 'usb');
        let filtered = printers;
        if (hasUsb) {
          const usbStatus = await checkUsbBridge();
          filtered = printers.filter(p => {
            if (p.connection_type === 'usb') return usbStatus.connected;
            return true;
          });
        }
        setPrinterList(filtered);
        const reachable = filtered.find(p => p.reachable);
        if (reachable) setSelectedPrinterId(reachable.id);
        else if (filtered.length) setSelectedPrinterId(filtered[0].id);
      } catch { /* 폴백 사용 */ }
    })();
  }, []);

  // 프린터 상태 폴링 (USB는 브릿지 앱에 직접 확인)
  const checkPrinter = useCallback(async () => {
    try {
      const res = await getPrinterStatus(selectedPrinterId);
      const printer = res.printer;

      if (printer?.connection_type === 'usb') {
        // USB: 서버 말고 브릿지 앱에 직접 물어봄
        const bridge = await checkUsbBridge();
        setPrinterStatus({
          ...printer,
          reachable: bridge.connected,
          status: bridge.connected ? 'ready' : 'unreachable',
        });
        // USB 에러는 팝업 안 뜸
      } else {
        setPrinterStatus(printer);
        if (printer?.status === 'ready') setPrinterError(null);
        else if (printer?.error) setPrinterError(printer.error);
      }
    } catch {
      setPrinterStatus(null);
    }
  }, [selectedPrinterId]);

  useEffect(() => {
    checkPrinter();
    pollRef.current = setInterval(checkPrinter, PRINTER_POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [checkPrinter]);

  // USB 리더기 refs (useEffect는 handleScanInput 뒤에)
  const usbBufferRef = useRef('');
  const usbTimerRef = useRef(null);

  // === 공통 유틸 ===
  const isCooldown = (key) => {
    const last = cooldownMap.current.get(key);
    return last && Date.now() - last < COOLDOWN_MS;
  };

  const addLog = (success, message) => {
    const id = Date.now();
    setLogs(prev => [{
      id, success, message,
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    }, ...prev].slice(0, MAX_LOGS));
    setTimeout(() => {
      setLogs(prev => prev.filter(log => log.id !== id));
    }, LOG_TTL_MS);
  };

  const getMemberLabel = (m) => {
    if (m.member_type === 'CHILD') return m.nickname || m.name || '자녀';
    return m.nickname || m.name || '보호자';
  };

  // === 가족 팝업 열기 (공통) ===
  const openFamilyPopup = async (memberId, memberInfo) => {
    setPopupLoading(true);
    setPopupError(null);
    setPopupMemberInfo(memberInfo);
    setFamilyData(null);
    setSelected(new Set());

    try {
      const familyRes = await getFamilyMembers(memberId, false);
      const parent = familyRes.parent;
      const children = familyRes.children || [];
      setFamilyData({ parent, children });

      // 전체 선택
      const allIds = new Set();
      allIds.add(parent.member_id);
      children.forEach(c => allIds.add(c.member_id));
      setSelected(allIds);

      setPopupOpen(true);
    } catch (err) {
      addLog(false, err.message || '가족 조회 실패');
    } finally {
      setPopupLoading(false);
    }
  };

  // === QR 스캔 입력 (카메라 + USB 리더기) ===
  const handleScanInput = useCallback(async (qrCode) => {
    if (popupOpen || popupLoading) return;
    if (isCooldown(qrCode)) {
      addLog(false, '이미 처리된 QR (5초 대기)');
      return;
    }
    cooldownMap.current.set(qrCode, Date.now());

    try {
      const memberRes = await getMemberByQR(qrCode);
      const member = memberRes.member;
      await openFamilyPopup(member.member_id, {
        name: member.nickname || member.name || (member.phone_masked ? member.phone_masked.slice(-4) : '회원'),
        phone_masked: member.phone_masked,
      });
    } catch (err) {
      addLog(false, err.message || '회원 조회 실패');
    }
  }, [popupOpen, popupLoading]);

  // USB 리더기: document keydown으로 직접 캡처
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'Enter') {
        e.preventDefault();
        const buffer = usbBufferRef.current.trim();
        usbBufferRef.current = '';
        if (usbTimerRef.current) { clearTimeout(usbTimerRef.current); usbTimerRef.current = null; }
        if (buffer.length > 0) {
          const qr = extractQRCode(buffer);
          if (qr) handleScanInput(qr);
        }
        return;
      }

      if (e.key.length === 1) {
        usbBufferRef.current += e.key;
        if (usbTimerRef.current) clearTimeout(usbTimerRef.current);
        usbTimerRef.current = setTimeout(() => { usbBufferRef.current = ''; usbTimerRef.current = null; }, 200);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (usbTimerRef.current) clearTimeout(usbTimerRef.current);
    };
  }, [handleScanInput]);

  // 네이티브 키오스크 앱에서 QR 코드 주입 수신
  useEffect(() => {
    const handleNativeScan = (e) => {
      const extracted = extractQRCode(e.detail);
      if (extracted) handleScanInput(extracted);
    };
    window.addEventListener('nativeQRScan', handleNativeScan);
    return () => window.removeEventListener('nativeQRScan', handleNativeScan);
  }, [handleScanInput]);

  // === 전화번호 조회 ===
  const formatPhone = (d) => {
    if (d.length === 0) return '';
    if (d.length <= 4) return `010-${d}`;
    return `010-${d.slice(0, 4)}-${d.slice(4)}`;
  };

  const handleKeyPress = (key) => {
    setPhoneError(null);
    if (key === 'backspace') setDigits(prev => prev.slice(0, -1));
    else if (key === 'clear') setDigits('');
    else setDigits(prev => prev.length < 8 ? prev + key : prev);
  };

  const handlePhoneSearch = async () => {
    if (digits.length !== 8) { setPhoneError('전화번호 뒤 8자리를 입력해주세요'); return; }
    if (popupOpen || popupLoading) return;

    setPopupLoading(true);
    setPhoneError(null);

    try {
      const memberRes = await getMemberByPhone('010' + digits);
      const member = memberRes.member;
      await openFamilyPopup(member.member_id, {
        name: member.nickname || member.name || (member.phone_masked ? member.phone_masked.slice(-4) : '회원'),
        phone_masked: member.phone_masked,
      });
      setDigits('');
    } catch (err) {
      setPhoneError(err.message || '조회 실패');
      setPopupLoading(false);
    }
  };

  // === 팝업 액션 ===
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const ids = new Set();
    if (familyData) {
      ids.add(familyData.parent.member_id);
      familyData.children.forEach(c => ids.add(c.member_id));
    }
    setSelected(ids);
  };

  const handlePopupPrint = async () => {
    if (selected.size === 0) return;
    setPrinting(true);
    setPopupError(null);
    try {
      const result = await printQrBatch([...selected], selectedPrinterId);
      if (result?.no_printer) {
        setPopupError(result.message || 'USB 프린터 미연결');
        return;
      }
      const countMsg = result.printed_count > 1 ? ` (${result.printed_count}장)` : '';
      addLog(true, `${result.label} - 출력 완료!${countMsg}`);
      handlePopupClose();
    } catch (err) {
      if (err.error_code?.startsWith('PRINTER_')) {
        // USB 프린터 에러는 로그만, 팝업 안 뜸
        const isUsb = printerList.find(p => p.id === selectedPrinterId)?.connection_type === 'usb';
        if (isUsb) {
          addLog(false, `USB 출력 실패: ${err.message}`);
          checkUsbBridge().then(bridgeStatus => {
            reportUsbPrintError({
              error_message: err.message,
              error_phase: 'bridge_error',
              printer_id: selectedPrinterId,
              bridge_status: bridgeStatus,
            });
          });
          handlePopupClose();
        } else {
          setPrinterError(err.message);
          checkPrinter();
          handlePopupClose();
        }
      } else {
        setPopupError(err.message || '출력 실패');
      }
    } finally {
      setPrinting(false);
    }
  };

  const handlePopupClose = () => {
    setPopupOpen(false);
    setFamilyData(null);
    setSelected(new Set());
    setPopupMemberInfo(null);
    setPopupError(null);
  };

  const allMembers = familyData ? [familyData.parent, ...familyData.children] : [];

  // 전체화면 진입
  const toggleFullscreen = () => {
    // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
    if (!isBridgeApp()) return;
    const el = document.documentElement;
    (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
  };

  return (
    <div className="qr-sticker-page">

      {/* 헤더 */}
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate('/')}>← 메뉴</button>
        <h1 className="page-header-title" onClick={toggleFullscreen}>QR 스티커</h1>
        <span className="header-store-name">
          {localStorage.getItem('store_name') || STORE_NAMES[localStorage.getItem('store_id')] || user?.store_name || ''}
        </span>
      </header>

      {/* 프린터 에러 오버레이 */}
      {printerError && (
        <div className="printer-error-overlay">
          <div className="printer-error-card">
            <span className="printer-error-icon">🔴</span>
            <h2>프린터 오류</h2>
            <p>{printerError}</p>
            {printerStatus && (
              <div className="printer-error-info">
                <p>{printerStatus.connection_type === 'usb' ? '🔌 USB' : (printerStatus.name || '프린터')} ({printerStatus.connection_type === 'usb' ? 'USB 연결' : printerStatus.ip})</p>
                <p>{printerStatus.model || 'QL-820NWB'}</p>
              </div>
            )}
            <div className="printer-error-buttons">
              <button className="btn-printer-retry" onClick={() => { setPrinterError(null); checkPrinter(); }}>
                다시 확인
              </button>
              <button className="btn-printer-dismiss" onClick={() => setPrinterError(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 메인 콘텐츠: 좌우 분할 */}
      <main className="sticker-content">
        {/* ═══ 좌측 패널: QR 카메라 스캐너 ═══ */}
        <div className="sticker-left-panel">
          <div className="sticker-left-header">
            <h2>QR 스캔</h2>
          </div>
          <div className="sticker-scanner-area">
            <QRScanner onScan={handleScanInput} enabled={!printerError && !popupOpen} />
            {(printing || popupLoading) && (
              <div className="sticker-printing-overlay">
                <div className="printing-spinner" />
                <p>{popupLoading ? '조회 중...' : '출력 중...'}</p>
              </div>
            )}
          </div>
          <p className="sticker-scan-guide">
            카메라에 QR을 비추거나<br/>QR 리더기로 스캔해주세요
          </p>
        </div>

        {/* ═══ 우측 패널: 전화번호 + 로그 ═══ */}
        <div className="sticker-right-panel">
          {/* 전화번호 입력 (항상 표시) */}
          <div className="phone-section">
            <p className="phone-section-title">전화번호로 가족 QR 출력</p>
            <div className="phone-display">
              <span>
                <span style={{color: '#999'}}>010</span>
                {digits.length > 0 ? `-${digits.slice(0, 4)}` : '-'}
                {digits.length > 4 ? `-${digits.slice(4)}` : ''}
                {digits.length === 0 && <span className="placeholder"> 뒤 8자리 입력</span>}
              </span>
            </div>
            <div className="numpad">
              {NUMPAD_KEYS.map((row, ri) => (
                <div className="numpad-row" key={ri}>
                  {row.map(key => (
                    <button key={key}
                      className={`numpad-key ${key === 'clear' ? 'key-clear' : ''} ${key === 'backspace' ? 'key-backspace' : ''}`}
                      onClick={() => handleKeyPress(key)}
                    >
                      {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                    </button>
                  ))}
                </div>
              ))}
              <button className="btn-phone-search" onClick={handlePhoneSearch}
                disabled={digits.length !== 8 || popupLoading}>
                {popupLoading ? '조회 중...' : '조회'}
              </button>
            </div>
            {phoneError && <div className="phone-error">{phoneError}</div>}
          </div>

          {/* 출력 로그 */}
          {logs.length > 0 && (
            <div className="print-logs">
              {logs.map(log => (
                <div key={log.id} className={`print-log ${log.success ? 'success' : 'error'}`}>
                  <span className="log-icon">{log.success ? '✅' : '❌'}</span>
                  <span className="log-message">{log.message}</span>
                  <span className="log-time">{log.time}</span>
                </div>
              ))}
            </div>
          )}

          {/* ═══ 가족 선택 팝업 (우측 패널 오버레이) ═══ */}
          {popupOpen && familyData && (
            <div className="family-popup-overlay">
              <div className="family-popup">
                <div className="family-popup-header">
                  <div className="family-popup-member-info">
                    <span className="family-popup-name">{popupMemberInfo?.name}</span>
                    <span className="family-popup-phone">{popupMemberInfo?.phone_masked}</span>
                  </div>
                  <span className="family-popup-badge">가족 {allMembers.length}명</span>
                </div>

                <div className="family-popup-toggle">
                  <button className="btn-toggle-all" onClick={() => {
                    if (selected.size === allMembers.length) setSelected(new Set());
                    else selectAll();
                  }}>
                    {selected.size === allMembers.length ? '☑️ 전체 해제' : '⬜ 전체 선택'}
                  </button>
                </div>

                <div className="family-popup-list">
                  {allMembers.map(m => (
                    <button key={m.member_id}
                      className={`family-popup-card ${selected.has(m.member_id) ? 'selected' : ''}`}
                      onClick={() => toggleSelect(m.member_id)}
                    >
                      <span className="family-popup-check">
                        {selected.has(m.member_id) ? '☑️' : '⬜'}
                      </span>
                      <span className="family-popup-card-name">{getMemberLabel(m)}</span>
                      <span className="family-popup-card-type">
                        {m.member_type === 'CHILD' ? '자녀' : '보호자'}
                      </span>
                    </button>
                  ))}
                </div>

                {popupError && (
                  <div className="family-popup-error">{popupError}</div>
                )}

                <div className="family-popup-actions">
                  <button className="btn-popup-print"
                    disabled={selected.size === 0 || printing}
                    onClick={handlePopupPrint}
                  >
                    {printing ? '출력 중...' : `🖨️ ${selected.size}장 출력`}
                  </button>
                  <button className="btn-popup-cancel" onClick={handlePopupClose}>
                    취소
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* 프린터 상태 바 */}
      <footer className="printer-status-bar">
        {printerList.length > 1 && (
          <select className="printer-select"
            value={selectedPrinterId || ''}
            onChange={e => setSelectedPrinterId(e.target.value ? Number(e.target.value) : null)}
          >
            {printerList.map(p => (
              <option key={p.id} value={p.id || ''}>
                {p.connection_type === 'usb' ? `🔌 USB` : p.name} {p.reachable ? '🟢' : '🔴'}
              </option>
            ))}
          </select>
        )}
        {printerStatus?.status === 'ready' && <span>🟢 {printerStatus.connection_type === 'usb' ? '🔌 USB' : (printerStatus.name || '프린터')} 연결됨</span>}
        {printerStatus?.status === 'unreachable' && <span>🔴 {printerStatus.connection_type === 'usb' ? '🔌 USB' : (printerStatus.name || '프린터')} 연결 안 됨</span>}
        {printerStatus?.status === 'disabled' && <span>⚪ 프린터 비활성화</span>}
        {!printerStatus && <span>프린터 상태 확인 중...</span>}
      </footer>
    </div>
  );
}
